/**
 * LayoutGallery.jsx — the sheet behind the "Layouts…" button: every slide shape
 * there is, grouped into the four collections, each one drawn with YOUR photos.
 *
 * A tile is not a diagram. It is the real thing at thumbnail size: the same
 * slideCells() rectangles, the same crops and the same fill colour the composer
 * and the export use, so what you tap is what you get. Spreads are drawn as the
 * run they are — every part, the photo carried across the seam — because a
 * single slide of one cannot show what it does.
 *
 * Controlled, because the composer bar owns its own state:
 *   <LayoutGallery open={open} onClose={() => setOpen(false)} />
 */

import { Fragment, useMemo } from 'react';
import { Dialog } from '@mui/material';
import {
  addOne, getState, selectImgById, setAddStyle, setLayoutAt, setPanoN, toast, useCarousel,
} from '../../hooks/useCarouselStore';
import { useMobile } from '../../hooks/useMobile';
import {
  cellLookAt, cellPlacement, effectiveCell, formatSize, makePano, newCell, slideCells, slideGeom,
} from '../../lib/carousel/geometry.js';
import { filterString, viewPhoto } from '../../lib/carousel/look.js';
import { MATS_PAINTED, layoutsByCollection } from '../../lib/carousel/layouts.js';
import './layouts.css';

// Percentages, so a tile scales with its column and still matches the slide.
function boxOf(rc, W, H) {
  return {
    left: (rc.x / W) * 100 + '%',
    top: (rc.y / H) * 100 + '%',
    width: (rc.w / W) * 100 + '%',
    height: (rc.h / H) * 100 + '%',
  };
}

/**
 * The thumbnail placed exactly as the cell will place the original — under the
 * photo's own look, like every other surface. Without this a frame you have just
 * turned or graded draws straight in the tile and turned in the slide the tile
 * adds, which is the one user this sheet is for: sizing is against the BAKED box
 * (a quarter turn swaps w/h) and the tile always paints the raw thumbnail, so the
 * CSS approximation is always wanted.
 */
function photoStyle(sl, im0, ci, cell, rc) {
  const look = cellLookAt(sl, im0, ci);
  const im = viewPhoto(im0, look);
  const pl = cellPlacement(im, rc.w, rc.h, cell, 'fill');
  const filter = filterString(look.adj);
  return {
    backgroundImage: 'url("' + im0.thumb + '")',
    backgroundSize: (im.w / pl.src.sw) * 100 + '% ' + (im.h / pl.src.sh) * 100 + '%',
    backgroundPosition: cell.ox * 100 + '% ' + cell.oy * 100 + '%',
    ...(filter ? { filter } : null),
  };
}

function MiniSlide({ sl, panos, imgById, design }) {
  const [W, H] = formatSize(design.fmt);
  const g = slideGeom(sl, design);
  const rects = slideCells(sl, W, H, g.pad, g.gap);
  return (
    <span className="lg-stage" style={{ aspectRatio: W + ' / ' + H, background: design.bg }}>
      {sl.cells.map((_, ci) => {
        const rc = rects[ci] || rects[0];
        const cell = effectiveCell(sl, ci, panos, imgById, W, H, design);
        const im = cell && cell.imgId ? imgById(cell.imgId) : null;
        // the fill ring an overlapping frame sits on, painted before its photo —
        // only while the composer and the export paint it too
        const mat = rc.mat && MATS_PAINTED
          ? boxOf({ x: rc.x - rc.mat, y: rc.y - rc.mat, w: rc.w + 2 * rc.mat, h: rc.h + 2 * rc.mat }, W, H)
          : null;
        return (
          <Fragment key={ci}>
            {mat && <span className="lg-mat" style={{ ...mat, background: design.bg }} />}
            <span
              className={'lg-cell' + (im ? '' : ' lg-blank')}
              style={{ ...boxOf(rc, W, H), ...(im ? photoStyle(sl, im, ci, cell, rc) : null) }}
            />
          </Fragment>
        );
      })}
    </span>
  );
}

/**
 * The slides a tile draws. A spread is built by the same makePano() the composer
 * uses, so the preview proves the run as well as showing it.
 */
function previewOf(l, pick) {
  let n = 0;
  const uid = () => 'lg' + ++n;
  if (l.spread) {
    const lead = pick(0);
    const p = makePano(lead ? lead.id : null, l.parts || 2, uid, l.id);
    // a spread part's extra cells are its own, so each part gets a different photo
    const slides = p.slides.map((sl, i) => ({
      ...sl,
      cells: sl.cells.map((c, ci) => {
        if (ci === 0) return c;
        const im = pick(1 + i * (l.cells - 1) + (ci - 1));
        return im ? newCell(im.id) : null;
      }),
    }));
    return { slides, panos: { [p.gid]: p.group } };
  }
  const cells = [];
  for (let i = 0; i < l.cells; i++) {
    const im = pick(i);
    cells.push(im ? newCell(im.id) : null);
  }
  return { slides: [{ id: 'lg', layout: l.id, cells }], panos: null };
}

/**
 * Adds a slide in this layout, through the store's own actions. A spread is a
 * panorama run, so it is added as one and its parts are then given the layout —
 * which is the one case where setLayout keeps the group.
 */
function addLayout(l) {
  const s = getState();
  const style = s.addStyle;
  if (!l.spread) {
    setAddStyle(l.id);
    addOne();
    setAddStyle(style);
    return true;
  }
  if (!s.sel.length) {
    toast('Select the wide photo first.');
    return false;
  }
  const { panoN } = s;
  const before = s.slides.length;
  // KNOWN GAP, deferred with Spreads themselves (layouts.js DEFERRED): addOne
  // fills only cells[0] of the run, so a Split Hero lands with its lower cells
  // empty although previewOf drew a photo under each half. Deal the rest of the
  // selection into them before this is offered again.
  setPanoN(l.parts || 2);
  setAddStyle('pano');
  addOne();
  const after = getState().slides;
  for (let i = before; i < after.length; i++) setLayoutAt(i, l.id);
  setPanoN(panoN);
  setAddStyle(style);
  return after.length > before;
}

export default function LayoutGallery({ open, onClose }) {
  const { isMobile } = useMobile();
  const design = useCarousel((s) => s.design);
  const library = useCarousel((s) => s.library);
  const sel = useCarousel((s) => s.sel);
  const imgById = useCarousel(selectImgById);

  // your own frames first, so a tile is a rehearsal rather than an illustration
  const pool = useMemo(() => {
    const chosen = sel.map(imgById).filter(Boolean);
    return chosen.length ? chosen : library.slice(0, 8);
  }, [sel, library, imgById]);

  const pick = (i) => (pool.length ? pool[i % pool.length] : null);
  const groups = useMemo(() => layoutsByCollection(), []);

  const note = !pool.length
    ? 'Add photos and the shapes fill with them.'
    : sel.length
      ? sel.length + (sel.length === 1 ? ' photo selected' : ' photos selected')
      : 'Drawn with the first photos in your list.';

  return (
    <Dialog
      open={!!open}
      onClose={onClose}
      fullScreen={isMobile}
      className="cc-modal"
      aria-labelledby="cc-layouts-title"
      slotProps={{
        paper: { className: 'cc-root export-sheet cc-layouts' + (isMobile ? ' is-phone' : '') },
        backdrop: { style: { background: 'rgba(12,12,14,.66)' } },
      }}
    >
      <div className="sheet-head">
        <h3 id="cc-layouts-title">Layouts</h3>
        <span className="hint">{note}</span>
        <button type="button" className="btn tiny ghost" onClick={onClose}>Close</button>
      </div>
      <div className="lg-body">
        {groups.map((c) => (
          <section key={c.key} className="lg-group">
            <header className="lg-grouphead">
              <h4>{c.name}</h4>
              <span className="lbl">{c.note}</span>
            </header>
            <div className="lg-grid">
              {c.items.map((l) => {
                const pv = previewOf(l, pick);
                const needsPhoto = l.spread && !sel.length;
                return (
                  <button
                    key={l.id}
                    type="button"
                    className={'lg-tile' + (l.spread ? ' is-spread' : '')}
                    disabled={needsPhoto}
                    title={l.describe()}
                    onClick={() => { if (addLayout(l)) onClose(); }}
                  >
                    <span className="lg-shot">
                      {pv.slides.map((sl) => (
                        <MiniSlide key={sl.id} sl={sl} panos={pv.panos} imgById={imgById} design={design} />
                      ))}
                    </span>
                    <span className="lg-name">{l.name}</span>
                    <span className="lg-desc">
                      {needsPhoto ? 'Select the wide photo first' : l.describe()}
                    </span>
                  </button>
                );
              })}
            </div>
          </section>
        ))}
      </div>
    </Dialog>
  );
}
